import {GLOBAL} from './deprecated/global';
import {initMerchantAccount} from './features/account';
import {initAddressAutocomplete} from './features/address-auto-complete';
import {initCart} from './features/cart';
import {initCouponInput} from './features/coupon';
import {initCurrency} from './features/currency';
import {initCurrencySwitcher} from './features/currencySwitch';
import {initCustomOrderMessaging} from './features/customOrderMessaging';
import {initGiftCardInput} from './features/giftCard';
import {initLanguage} from './features/language';
import {initMerchantLogo} from './features/merchantLogo';
import {initModal} from './features/modal';
import {initOneClickUpsell} from './features/oneClickUpsell';
import {initPaymentGatewaySelector} from './features/paymentSelector';
import {initRelatedProducts} from './features/relatedProducts';
import {initSummary} from './features/summary';
import {initVAT} from './features/vat';
import {initBotProtection} from './features/botProtection';
import {consumeCartCalculationResponse, getOrderService, requestCartCalculation, type OrderService} from './payment/order';
import {initPeachPayMethods} from './payment/peachpay/peachpay';
import {FeatureFlag} from '../../@type/features';
import {Feature, setFeatureSupport, startModalLoading, stopModalLoading} from './reducers/environmentReducer';
import {store} from './store';
import {initSentry} from '../../@shared/ts/sentry';
import {PLUGIN_VERSION} from '../../git';
import {type CheckoutData} from './models/CheckoutData';
import {initSDKEvents} from './sdk';
import {initBillingFormEvents} from './features/fields/billingForm';
import {initShippingFormEvents} from './features/fields/shippingForm';
import {initShippingOptionsFormEvents} from './features/fields/shippingOptionsForm';
import {initFormLocaleEvents} from './features/fields/addressLocale';
import {doAction} from '../../@shared/ts/hooks';
import initConvesioPayPaymentIntegration, {registerConvesioPayGatewayMetadata} from './payment/convesiopay/convesiopay';
import {createPaymentIntegrationReadiness, registerPaymentIntegrationReadiness} from './payment/lazy-integrations';

declare const checkout_data: CheckoutData;
(window as any).store = store;

initSentry(`peachpay-checkout@${PLUGIN_VERSION}`, 'https://39b5a2e17e264bb5a6ea5abe9bc6cf61@o470066.ingest.sentry.io/5660513');

const importGateway = {
	authnet: {
		featureFlag: FeatureFlag.AUTHNET_GATEWAY,
		import_: async () => import('./payment/authnet/authnet'),
	},
	stripe: {
		featureFlag: FeatureFlag.STRIPE_GATEWAYS,
		import_: async () => import('./payment/stripe/stripe'),
	},
	square: {
		featureFlag: FeatureFlag.SQUARE_GATEWAYS,
		import_: async () => import('./payment/square/square'),
	},
	paypal: {
		featureFlag: FeatureFlag.PAYPAL_GATEWAYS,
		import_: async () => import('./payment/paypal/paypal'),
	},
	convesiopay: {
		featureFlag: FeatureFlag.CONVESIOPAY_GATEWAYS,
		import_: async () => import('./payment/convesiopay/convesiopay'),
	},
} satisfies Record<string, GatewayIntegrationImport>;

/**
 * Entry point for the peachpay checkout.
 */
async function main() {
	initSDKEvents();

	store.dispatch(startModalLoading());

	GLOBAL.checkoutData = checkout_data;

	// We first need to initialize the language for use in the form fields
	store.dispatch(setFeatureSupport(checkout_data.feature_support));
	initLanguage();

	/**
	 * Feature callbacks to perform a action or insert data into the redux store once the data is available.
	 */
	initBillingFormEvents();
	initFormLocaleEvents(checkout_data, 'billing');

	initShippingFormEvents();
	initFormLocaleEvents(checkout_data, 'shipping');

	initShippingOptionsFormEvents();
	initOneClickUpsell();
	initCart();
	initBotProtection();
	initSummary(checkout_data);
	initCouponInput();
	initGiftCardInput();
	initCurrency(checkout_data);
	initMerchantAccount(checkout_data);
	initVAT(checkout_data);
	initRelatedProducts();
	initCurrencySwitcher();
	initPaymentGatewaySelector();
	initModal(checkout_data);
	initAddressAutocomplete();
	initCustomOrderMessaging();
	initMerchantLogo();

	// Payment Processor support
	const orderService = getOrderService();

	initPeachPayMethods(orderService);
	registerConvesioPayGatewayMetadata();

	registerPaymentIntegrationReadiness(createPaymentIntegrationReadiness([
		{name: 'stripe', initialize: async () => importPaymentIntegration(importGateway.stripe, orderService)},
		{name: 'authnet', initialize: async () => importPaymentIntegration(importGateway.authnet, orderService)},
		{name: 'square', initialize: async () => importPaymentIntegration(importGateway.square, orderService)},
		{name: 'paypal', initialize: async () => importPaymentIntegration(importGateway.paypal, orderService)},
		{name: 'convesiopay', initialize: async () => initConvesioPayPaymentIntegration(orderService)},
	]));

	if (checkout_data.cart_calculation_response) {
		consumeCartCalculationResponse(checkout_data.cart_calculation_response);
	} else if (isTopWindow()) {
		await requestCartCalculation('pull', 'boot:standalone-pull', {cartCalculationDedupeKey: 'first-open'});
	}

	window.dispatchEvent(new CustomEvent('pp-update-afterpay-branding'));

	await doAction('after_modal_open');
	store.dispatch(stopModalLoading());
}

document.addEventListener('DOMContentLoaded', main);

type GatewayIntegrationImport = {
	featureFlag: FeatureFlag;
	import_: () => Promise<{default: (orderService: OrderService) => Promise<void> | void}>;
};

/**
 * Import payment dynamically based on given information.
 *
 * @param importGateway
 * @param orderService
 */
async function importPaymentIntegration(importGateway: GatewayIntegrationImport, orderService: OrderService): Promise<void> {
	if (!Feature.enabled(importGateway.featureFlag)) {
		return;
	}

	return new Promise<void>((resolve, reject) => {
		importGateway.import_()
			.then(gateway => {
				Promise.resolve(gateway.default(orderService)).then(resolve).catch(reject);
			})
			.catch(e => {
				console.error(`Failed to import payment integration: ${importGateway.featureFlag}`, e);
				reject(e);
			});
	});
}

function isTopWindow(): boolean {
	try {
		return window.self === window.top;
	} catch {
		return false;
	}
}
